// Config Imports
import themeConfig from './themeConfig'

// Type Imports
import type { FontKey } from '@/utils/fonts'
import type { ThemePresetKey } from '@/utils/theme-presets'

export const MODE_VALUES = ['light', 'dark', 'system'] as const
export const COLLAPSIBLE_VALUES = ['expanded', 'icon', 'hidden'] as const
export const VARIANT_VALUES = ['flush', 'inset', 'floating'] as const
export const RADIUS_VALUE_KEYS = ['none', 'sm', 'md', 'lg'] as const
export const LAYOUT_VALUES = ['compact', 'full'] as const
export const SCALE_VALUES = ['sm', 'md', 'lg'] as const

export type Mode = (typeof MODE_VALUES)[number]
export type Collapsible = (typeof COLLAPSIBLE_VALUES)[number]
export type Variant = (typeof VARIANT_VALUES)[number]
export type Radius = (typeof RADIUS_VALUE_KEYS)[number]
export type Layout = (typeof LAYOUT_VALUES)[number]
export type Scale = (typeof SCALE_VALUES)[number]
export type { FontKey, ThemePresetKey }

export type Settings = {
  mode: Mode
  collapsible: Collapsible
  variant: Variant
  sidebarOpen: boolean
  themePreset: ThemePresetKey
  radius: Radius
  layout: Layout
  scale: Scale
  font: FontKey
}

export const RADIUS_VALUES: Record<Radius, string> = {
  none: '0rem',
  sm: '0.45rem',
  md: '0.625rem',
  lg: '0.875rem'
}

export const SIDEBAR_VARIANT_PROP: Record<Variant, 'sidebar' | 'inset' | 'floating'> = {
  flush: 'sidebar',
  inset: 'inset',
  floating: 'floating'
}

export const SIDEBAR_COLLAPSIBLE_PROP: Record<Collapsible, 'none' | 'icon' | 'offcanvas'> = {
  expanded: 'none',
  icon: 'icon',
  hidden: 'offcanvas'
}

export const initialSettings: Settings = {
  mode: themeConfig.mode,
  themePreset: themeConfig.themePreset as ThemePresetKey,
  font: themeConfig.font as FontKey,
  radius: themeConfig.radius,
  scale: themeConfig.scale,
  layout: themeConfig.layout,
  variant: themeConfig.sidebarVariant,
  collapsible: themeConfig.sidebarCollapsible,
  sidebarOpen: themeConfig.sidebarOpen
}

const ALLOWED_VALUES = {
  mode: MODE_VALUES,
  collapsible: COLLAPSIBLE_VALUES,
  variant: VARIANT_VALUES,
  radius: RADIUS_VALUE_KEYS,
  layout: LAYOUT_VALUES,
  scale: SCALE_VALUES
} as const

export const sanitizeSettings = (raw: unknown): Partial<Settings> => {
  if (!raw || typeof raw !== 'object') return {}

  const source = raw as Record<string, unknown>
  const clean: Record<string, unknown> = {}

  for (const key of Object.keys(initialSettings) as (keyof Settings)[]) {
    if (!(key in source)) continue

    const value = source[key]
    const allowed = ALLOWED_VALUES[key as keyof typeof ALLOWED_VALUES] as readonly unknown[] | undefined

    if (allowed ? allowed.includes(value) : typeof value === typeof initialSettings[key]) {
      clean[key] = value
    }
  }

  return clean as Partial<Settings>
}
